import React, { useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

interface NewsletterSignupCardProps {
  onSubscribe?: (email: string, agreed: boolean) => void;
}

const NewsletterSignupCard: React.FC<NewsletterSignupCardProps> = ({
  onSubscribe,
}) => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [agreed, setAgreed] = useState(false);

  const validateEmail = (value: string) => {
    if (!value.trim()) {
      return "Email address is required";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(value)) {
      return "Please enter a valid email address";
    }

    return "";
  };

  const handleEmailChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;

    setEmail(value);

    // Clear error while typing
    if (emailError) {
      setEmailError(validateEmail(value));
    }
  };

  const handleSubscribe = () => {
    const error = validateEmail(email);

    if (error) {
      setEmailError(error);
      return;
    }

    setEmailError("");
    onSubscribe?.(email, agreed);
  };

  return (
    <Card
      elevation={0}
      sx={{
        width: "100%",
        py: 2, px: 2.5,
        borderRadius: "10px",
        backgroundColor: "#fff",
        border: "1px solid #e2e6ed",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
        mb: 2,
      }}
    >
      {/* Heading */}
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: "#14181f",
          mb: 1,
        }}
      >
        Let&apos;s keep in touch
      </Typography>

      {/* Subheading */}
      <Typography
        variant="body2"
        sx={{ color: '#667085', mb: 2, lineHeight: 1.4 }}
      >
        Subscribe to keep up with fresh news and exciting updates.
      </Typography>

      {/* Promise label */}
      <Typography
        variant="body2"
        sx={{ color: '#667085', mb: 1, lineHeight: 1.4 }}
      >
        We promise not to spam you!
      </Typography>

      {/* Email Address */}
      <TextField
        fullWidth
        required
        size="small"
        type="email"
        placeholder="Enter your email address"
        value={email}
        onChange={handleEmailChange}
        error={Boolean(emailError)}
        helperText={emailError}
        onBlur={() => {
          setEmailError(validateEmail(email));
        }}
        sx={{
          marginBottom: emailError ? "8px" : "16px",

          "& .MuiOutlinedInput-root": {
            height: "48px",
            borderRadius: "6px",
            fontSize: 14,
            paddingRight: "6px",

            "& fieldset": {
              borderColor: "#d7dce5",
            },

            "&:hover fieldset": {
              borderColor: "#b9c1ce",
            },

            "&.Mui-focused fieldset": {
              borderColor: "#2AAE56",
            },

            "&.Mui-error fieldset": {
              borderColor: "#d32f2f",
            },
          },

          "& input::placeholder": {
            color: "#64748b",
            opacity: 1,
          },

          "& .MuiFormHelperText-root": {
            marginLeft: "2px",
            fontSize: "11px",
          },
        }}
        InputProps={{
          endAdornment: (
            <Button
              onClick={handleSubscribe}
              sx={{
                minWidth: "36px",
                width: "36px",
                height: "36px",
                borderRadius: "6px",
                backgroundColor: "#213e99",
                color: "#fff",
                p: 0,

                "&:hover": {
                  backgroundColor: "#172554",
                },
              }}
            >
              <ArrowForwardIcon sx={{ fontSize: 18 }} />
            </Button>
          ),
        }}
      />

      {/* Consent Checkbox */}
      <FormControlLabel
        control={
          <Checkbox
            checked={agreed}
            onChange={(event) => setAgreed(event.target.checked)}
            size="small"
            sx={{
              color: '#D0D5DD',
              alignSelf: 'flex-start',
              mt: 0.5,
              p: 0,
              '&.Mui-checked': {
                color: '#1E3A8A',
              },
            }}
          />
        }
        label={
          <Typography variant="caption" sx={{ color: '#667085', mt: 0, ml: 1, lineHeight: 1.3 }}>
            I agree to my email address being stored and used to receive
            monthly newsletter.
          </Typography>
        }
        sx={{
          alignItems: "flex-start",
          ml: 0,
        }}
      />
    </Card>
  );
};

export default NewsletterSignupCard;
